import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Send, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES, EMIRATES, EXPECTED_DATES, CONTACT_METHODS } from "../lib/i18n";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";

const emptyForm = {
  name: "", phone: "", project_type: "restaurant", emirate: "dubai", message: "",
  company_name: "", email: "", budget: "", expected_date: "not_sure", company_website: "",
  preferred_contact: "whatsapp", boq_file_path: "", boq_file_name: "", project_reference: "",
};

export function QuotationForm({ projectReference = "", onSuccess, redirectOnSuccess = true }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...emptyForm, project_reference: projectReference });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [fileError, setFileError] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError("");
    if (file.size > 10 * 1024 * 1024) {
      setFileError("Max 10MB");
      return;
    }
    const okTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (!okTypes.includes(file.type)) {
      setFileError("PDF / JPG / PNG only");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload/quotation", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("boq_file_path", data.path);
      update("boq_file_name", data.filename);
      toast.success(`Uploaded: ${data.filename}`);
    } catch (err) {
      setFileError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    update("boq_file_path", "");
    update("boq_file_name", "");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotations", form);
      onSuccess?.();
      if (redirectOnSuccess) {
        navigate("/thank-you");
        return;
      }
      toast.success(t.quotation.success);
      setDone(true);
    } catch {
      toast.error(t.quotation.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="quotation-success">
        <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-5" />
        <h3 className="font-display text-2xl text-[#0891b2] mb-3">{t.quotation.success}</h3>
        <Button
          data-testid="quotation-new-btn"
          onClick={() => {
            setForm({ ...emptyForm, project_reference: projectReference });
            setDone(false);
          }}
          className="bg-[#0891b2] hover:bg-[#ef4444] text-white"
        >
          Submit another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="quotation-form">
      {projectReference && (
        <div className="bg-cyan-50 border border-blue-200 text-blue-900 p-3 text-sm rounded">
          <span className="font-medium">{t.quotation.referenceLabel}</span> {projectReference}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.quotation.name} required>
          <Input required value={form.name} onChange={(e) => update("name", e.target.value)}
                 data-testid="q-name" className="h-11" />
        </Field>
        <Field label={t.quotation.phone} required>
          <Input required dir="ltr" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                 data-testid="q-phone" className="h-11" placeholder="+971 4-288-2777" />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.quotation.projectType} required>
          <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
            <SelectTrigger data-testid="q-project-type" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t.categories[c]}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t.quotation.emirate} required>
          <Select value={form.emirate} onValueChange={(v) => update("emirate", v)}>
            <SelectTrigger data-testid="q-emirate" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EMIRATES.map((e) => <SelectItem key={e} value={e}>{t.emirates[e]}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label={t.quotation.message} required>
        <Textarea required rows={4} value={form.message} onChange={(e) => update("message", e.target.value)}
                  data-testid="q-message" />
      </Field>

      <div className="pt-2 border-t border-slate-200 text-xs uppercase text-slate-500 tracking-wider">
        {t.quotation.optional}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.quotation.companyName}>
          <Input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} className="h-11" data-testid="q-company" />
        </Field>
        <Field label={t.quotation.email}>
          <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-11" data-testid="q-email" />
        </Field>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Field label={t.quotation.budget}>
          <Input value={form.budget} onChange={(e) => update("budget", e.target.value)} className="h-11" placeholder="50,000+" />
        </Field>
        <Field label={t.quotation.expectedDate}>
          <Select value={form.expected_date} onValueChange={(v) => update("expected_date", v)}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EXPECTED_DATES.map((d) => <SelectItem key={d} value={d}>{t.quotation.dates[d]}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t.quotation.website}>
          <Input dir="ltr" value={form.company_website} onChange={(e) => update("company_website", e.target.value)} className="h-11" placeholder="https://" />
        </Field>
      </div>

      <Field label={t.quotation.preferredContact}>
        <RadioGroup value={form.preferred_contact} onValueChange={(v) => update("preferred_contact", v)}
                    className="flex flex-wrap gap-4">
          {CONTACT_METHODS.map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
              <RadioGroupItem value={m} data-testid={`q-contact-${m}`} />
              {t.quotation.contact[m]}
            </label>
          ))}
        </RadioGroup>
      </Field>

      <Field label={t.quotation.uploadLabel}>
        <div>
          {form.boq_file_name ? (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-3 text-sm">
              <span className="text-slate-700 truncate" dir="ltr">📎 {form.boq_file_name}</span>
              <button type="button" onClick={clearFile} className="text-slate-500 hover:text-red-600" data-testid="q-file-clear">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded p-6 cursor-pointer hover:border-[#0891b2] hover:bg-slate-50 transition-colors">
              <Upload className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-600">
                {uploading ? "Uploading..." : t.quotation.uploadLabel}
              </span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} disabled={uploading}
                     data-testid="q-file-input" className="hidden" />
            </label>
          )}
          <p className="text-xs text-slate-500 mt-1.5">{t.quotation.uploadHint}</p>
          {fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
        </div>
      </Field>

      <Button
        type="submit"
        disabled={submitting || uploading}
        data-testid="q-submit"
        className="w-full sm:w-auto bg-[#ef4444] hover:bg-[#dc2626] text-white font-medium h-12 px-10"
      >
        {submitting ? t.quotation.submitting : t.quotation.submit}
        {!submitting && <Send className="w-4 h-4 ms-2" />}
      </Button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
